import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { PublicLayout } from "@/components/layout/PublicLayout";
import { GlassCard } from "@/components/GlassCard";
import { SectorBadge } from "@/components/Badges";
import SEO from "@/components/SEO";
import api from "@/lib/api";
import { container, SECTORS } from "@/lib/constants";

export default function CaseStudyDetail() {
  const { slug } = useParams();
  const [s, setS] = useState(null);
  const [notFound, setNotFound] = useState(false);
  useEffect(() => {
    api.get(`/public/case-studies/${slug}`).then((r) => setS(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) return (
    <PublicLayout>
      <div className={`${container} py-32 text-center`}>
        <h1 className="pdg-serif text-3xl text-foreground">Case study not found</h1>
        <Link to="/case-studies" className="mt-6 inline-block text-sm text-[hsl(var(--pdg-gold))]">Back to case studies</Link>
      </div>
    </PublicLayout>
  );
  if (!s) return <PublicLayout><div className={`${container} py-32 text-sm text-muted-foreground`}>Loading…</div></PublicLayout>;

  const jsonLd = {
    "@context": "https://schema.org", "@type": "Article", headline: s.title,
    about: SECTORS[s.sector], articleSection: s.deal_type,
    description: s.outcome, publisher: { "@type": "Organization", name: "Prime Deal Gateway" },
  };

  return (
    <PublicLayout>
      <SEO title={s.title} description={s.outcome.slice(0, 155)} jsonLd={jsonLd} />
      <article className={`${container} py-16 lg:py-24`}>
        <Link to="/case-studies" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft size={15} /> All case studies
        </Link>
        <div className="mt-8 grid gap-10 lg:grid-cols-12">
          <div className="lg:col-span-8">
            <SectorBadge sector={s.sector} />
            <h1 className="mt-5 pdg-serif text-4xl sm:text-5xl text-foreground leading-[1.08]">{s.title}</h1>
            <div className="mt-8 pdg-hairline w-20" />
            <p className="mt-8 text-lg text-foreground/85 leading-9">{s.outcome}</p>
            <p className="mt-6 text-sm text-muted-foreground leading-8">
              This case study has been anonymised. Names, precise locations, and identifying figures
              have been withheld in keeping with the desk&rsquo;s standard of discretion.
            </p>
          </div>
          <aside className="lg:col-span-4">
            <GlassCard className="p-7">
              <div className="pdg-kicker mb-5">Deal facts</div>
              <dl className="space-y-4 text-sm">
                {[["Sector", SECTORS[s.sector]], ["Deal type", s.deal_type], ["Geography", s.geography], ["Size range", s.size_range]].map(([k, v]) => (
                  <div key={k} className="flex justify-between gap-4 border-b border-border pb-3">
                    <dt className="text-muted-foreground">{k}</dt>
                    <dd className="text-foreground text-right">{v}</dd>
                  </div>
                ))}
              </dl>
            </GlassCard>
          </aside>
        </div>
      </article>
    </PublicLayout>
  );
}
